import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePolyAuth } from '../../../hooks/PolyAuthContext';

/**
 * Fetches POST /v2/logs/meeting/details for the selected meeting and shapes
 * the participant payload for the modal's table. Only runs for callType=meeting
 * rows that have both meeting_id and meeting_uuid; trust-check rows render
 * straight from selectedLog without a fetch.
 */
export function useMeetingDetails(selectedLog) {
    const { t } = useTranslation('shared');
    const { api_client } = usePolyAuth();

    const [meetingDetails, setMeetingDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [detailsError, setDetailsError] = useState(null);

    useEffect(() => {
        if (!selectedLog) return;
        const shouldFetch =
            selectedLog.callType === 'meeting' &&
            selectedLog.meeting_id &&
            selectedLog.meeting_uuid;
        if (!shouldFetch) return;

        let cancelled = false;
        (async () => {
            setLoadingDetails(true);
            setDetailsError(null);
            try {
                const meetingResponse = await api_client.post('/v2/logs/meeting/details', {
                    meeting_id: selectedLog.meeting_id,
                    meeting_uuid: selectedLog.meeting_uuid,
                    start_time: selectedLog.start_time,
                });
                if (cancelled) return;
                const response = meetingResponse.data;

                const formattedParticipants = response.participants.map((participant) => {
                    const verification_data = participant.verification_data || {};
                    const poly_user_id = participant.poly_user_id || '';
                    const isUnverified = !poly_user_id || poly_user_id === 'unknown-00000';
                    return {
                        name: participant.user_name || t('callLogs.unknown'),
                        id: participant.id || '',
                        join_time: participant.join_time || '',
                        leave_time: participant.leave_time || '',
                        role: participant.user_role || 'participant',
                        poly_user_id,
                        is_rejoin: participant.is_rejoin || false,
                        isUnverified,
                        verification: {
                            phone_verified: verification_data.phone_number_verification_id || 'Unknown',
                            certainty: verification_data.certainty || 'Unknown',
                            hardware_key: verification_data.hardware_attestation_id || 'Unknown',
                            document_id: verification_data.id_verification_id || 'Unknown',
                        },
                    };
                });

                setMeetingDetails({
                    ...response,
                    participants: {
                        list: formattedParticipants,
                        total: formattedParticipants?.length ?? 0,
                    },
                });
            } catch (error) {
                if (cancelled) return;
                console.error('Error fetching meeting details:', error);
                setDetailsError(t('callLogs.failedToLoadDetails'));
            } finally {
                if (!cancelled) setLoadingDetails(false);
            }
        })();

        return () => { cancelled = true; };
    }, [selectedLog, api_client, t]);

    const reset = () => setMeetingDetails(null);

    return { meetingDetails, loadingDetails, detailsError, reset };
}
